import cx from "classnames";
import type { ReactNode } from "react";

import { Link } from "metabase/common/components/Link";
import { Ellipsified, Group, Icon } from "metabase/ui";
import type { ColorName } from "metabase/ui/colors";
import { color as getColor } from "metabase/ui/utils/colors";
import type { IconName } from "metabase-types/api";

import S from "./Breadcrumb.module.css";

/** For a single-line flex trail: its direct `Breadcrumb` children truncate the longest crumbs first. */
export const BREADCRUMB_TRAIL_CLASS_NAME = S.trail;

interface Props {
  children: ReactNode;
  className?: string;
  color?: ColorName;
  icon?: IconName;
  iconClassName?: string;
  iconColor?: ColorName;
  iconSize?: number;
  showTooltip?: boolean;
  to?: string;
  onClick?: () => void;
}

export const Breadcrumb = ({
  children,
  className,
  color,
  icon,
  iconClassName,
  iconColor,
  iconSize,
  showTooltip = false,
  to,
  onClick,
}: Props) => {
  const isLink = typeof to === "string" && to.length > 0;

  const content = (
    <Group
      align="center"
      className={cx(S.content, {
        [S.clickable]: isLink || onClick,
      })}
      gap="xxs"
      style={{
        "--color": color ? getColor(color) : undefined,
        "--active-color": iconColor ? getColor(iconColor) : undefined,
      }}
      wrap="nowrap"
    >
      {icon && (
        <Icon
          c={iconColor}
          className={cx(S.icon, iconClassName)}
          flex="0 0 auto"
          name={icon}
          size={iconSize}
        />
      )}

      <Ellipsified
        c={undefined} // to avoid inline style with would have higher specificity than CSS in Breadcrumb.module.css
        className={S.text}
        fw="bold"
        lh="normal"
        showTooltip={showTooltip}
      >
        {children}
      </Ellipsified>
    </Group>
  );

  if (isLink) {
    return (
      <Link className={cx(S.breadcrumb, className)} to={to} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <span className={cx(S.breadcrumb, className)} onClick={onClick}>
      {content}
    </span>
  );
};
